import { useState } from "react";
import { Icon } from "../icons";
import LogSessionModal from "../components/LogSessionModal";
import WorkoutDetailModal from "../components/WorkoutDetailModal";
import { HoldToDelete } from "../components/shared";
import { weekStart, weekEnd, fmtWeight, fmtVol, wtLabel } from "../helpers";

const NOISE = "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const ACCENTS     = ["#94a3b8", "#e1c19d"];
const ACCENT_BKGS = ["rgba(148,163,184,0.15)", "rgba(225,193,157,0.15)"];

export default function WorkoutsView({ workouts, allExercises, onEdit, onDelete, onLogSession, units = "lbs" }) {
  const [logging, setLogging]   = useState(null);
  const [selected, setSelected] = useState(null);

  const ws = weekStart(), we = weekEnd();
  const weekVol = allExercises.reduce((sum, ex) =>
    sum + ex.sets.filter(s => s.date >= ws && s.date <= we)
                 .reduce((a, s) => a + s.reps * s.weight, 0), 0);

  return (
    <div className="fade-in">

      {/* ── WORKOUT SECTIONS ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
        {workouts.length === 0 ? (
          <div style={{ background: "var(--bg-low)", borderRadius: 8, padding: "40px 20px", textAlign: "center" }}>
            <div className="label-caps" style={{ marginBottom: 6 }}>NO WORKOUTS YET</div>
            <div style={{ fontSize: 12, color: "var(--muted)" }}>Create a routine using the + button above</div>
          </div>
        ) : workouts.map((wk, wkIndex) => {
          const accent    = ACCENTS[wkIndex % ACCENTS.length];
          const accentBkg = ACCENT_BKGS[wkIndex % ACCENT_BKGS.length];
          const wkExIds   = new Set((wk.exercises || []).map(Number));
          const exList    = allExercises.filter(e => wkExIds.has(Number(e.id)));
          const muscles   = wk.muscles || [...new Set(exList.map(e => e.muscle).filter(Boolean))];
          const isPrimary = wkIndex === 0;

          return (
            <section key={wk.id} style={{ position: "relative" }}>
              {/* Vertical accent line */}
              <div style={{
                position: "absolute", left: -12, top: 0, bottom: 0,
                width: 3, background: `${accent}30`, borderRadius: 2,
              }}/>

              {/* Section header row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 14 }}>
                <div>
                  <h3 style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em",
                    textTransform: "uppercase", color: "var(--text)", marginBottom: 2,
                  }}>{wk.name}</h3>
                  <p style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 10, color: "#c4c6cd", letterSpacing: "0.15em",
                    textTransform: "uppercase",
                  }}>{muscles.slice(0, 3).join(", ") || "—"}</p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 10, color: accent, background: accentBkg,
                    padding: "3px 8px", borderRadius: 4, letterSpacing: "0.12em", flexShrink: 0,
                  }}>{exList.length} EXERCISES</span>
                  <button
                    onClick={e => { e.stopPropagation(); onEdit(wk); }}
                    style={{ background: "transparent", border: "none", color: "#64748b", cursor: "pointer", padding: "4px 5px", borderRadius: 4, display: "flex" }}
                  ><Icon.Edit/></button>
                  <HoldToDelete onDelete={() => onDelete(wk.id)} />
                </div>
              </div>

              {/* Card */}
              <div
                onClick={() => setSelected(wk)}
                style={{
                  background: "var(--bg-low)", borderRadius: 8,
                  padding: 20, position: "relative", overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                <div style={{ position: "absolute", inset: 0, backgroundImage: NOISE, opacity: 0.03, pointerEvents: "none" }}/>

                {/* Exercise rows */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14, position: "relative" }}>
                  {exList.length === 0 ? (
                    <p style={{ textAlign: "center", fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "#64748b", letterSpacing: "0.12em", padding: "8px 0" }}>
                      NO EXERCISES IN THIS WORKOUT
                    </p>
                  ) : exList.slice(0, 3).map((ex, i) => {
                    const bestSet = ex.sets.reduce((b, s) => s.weight > (b?.weight || 0) ? s : b, null);
                    const isLast  = i === Math.min(exList.length - 1, 2);
                    return (
                      <div key={ex.id} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        ...(!isLast ? { borderBottom: "1px solid rgba(68,71,76,0.12)", paddingBottom: 14 } : {}),
                      }}>
                        <div>
                          <p style={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontWeight: 500, textTransform: "uppercase", fontSize: 12, color: "var(--text)",
                          }}>{ex.name}</p>
                          <p style={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontSize: 10, color: "#c4c6cd", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 1,
                          }}>
                            {ex.muscles?.[0] || ex.muscle || "—"} · {ex.sets.length} SETS
                          </p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <p style={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontWeight: 700, color: accent, fontSize: 14,
                          }}>{bestSet ? `${fmtWeight(bestSet.weight, units)} ${wtLabel(units)}` : "—"}</p>
                          <p style={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontSize: 9, color: "#c4c6cd", textTransform: "uppercase", letterSpacing: "0.1em",
                          }}>BEST WEIGHT</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* LOG SESSION button */}
                <button
                  onClick={e => { e.stopPropagation(); setLogging(wk); }}
                  style={{
                    marginTop: 18, width: "100%", padding: 14, borderRadius: 8,
                    background: isPrimary
                      ? "linear-gradient(135deg, #b9c8de 0%, #94a3b8 100%)"
                      : "transparent",
                    border: isPrimary ? "none" : "1px solid rgba(68,71,76,0.25)",
                    color: isPrimary ? "#233143" : "var(--text)",
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700, fontSize: 12, letterSpacing: "0.15em",
                    textTransform: "uppercase", cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >LOG SESSION</button>
              </div>
            </section>
          );
        })}
      </div>

      {/* ── WEEKLY METRICS ── */}
      <section style={{ marginTop: 44, marginBottom: 16 }}>
        <h4 style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: 10, color: "#c4c6cd", letterSpacing: "0.2em",
          fontWeight: 700, textTransform: "uppercase", marginBottom: 14, textAlign: "center",
        }}>WEEKLY METRICS</h4>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
          <div style={{
            background: "var(--bg-highest)", padding: "14px 16px", borderRadius: 8,
            height: 112, display: "flex", flexDirection: "column", justifyContent: "space-between",
          }}>
            <Icon.Activity />
            <div>
              <p style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 26, fontWeight: 700, letterSpacing: "-0.04em", color: "var(--text)",
              }}>{(() => { const v = fmtVol(weekVol, units); return v > 999 ? `${(v/1000).toFixed(1)}K` : v; })()}</p>
              <p style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 9, color: "#c4c6cd", textTransform: "uppercase", letterSpacing: "0.15em",
              }}>VOLUME ({wtLabel(units)})</p>
            </div>
          </div>
          <div style={{
            background: "var(--bg-low)", padding: "14px 16px", borderRadius: 8,
            height: 112, display: "flex", flexDirection: "column", justifyContent: "space-between",
            border: "1px solid rgba(68,71,76,0.12)",
          }}>
            <span style={{ color: "#e1c19d", fontSize: 18, lineHeight: 1 }}>⚡</span>
            <div>
              <p style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 22, fontWeight: 700, letterSpacing: "-0.04em", color: "var(--text)",
              }}>{workouts.length}</p>
              <p style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 9, color: "#c4c6cd", textTransform: "uppercase", letterSpacing: "0.15em",
              }}>ROUTINES</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── MODALS ── */}
      {logging && (
        <LogSessionModal
          userExercises={allExercises}
          userWorkouts={workouts}
          workout={logging}
          units={units}
          onClose={() => setLogging(null)}
          onSave={session => { onLogSession(session); setLogging(null); }}
        />
      )}
      {selected && (
        <WorkoutDetailModal
          workout={selected}
          allExercises={allExercises}
          onClose={() => setSelected(null)}
          onEdit={wk => { setSelected(null); onEdit(wk); }}
          onDelete={id => { setSelected(null); onDelete(id); }}
        />
      )}
    </div>
  );
}
